import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import { Sheet } from "@/components/ui/dialog";
import { Toast, type ToastMessage } from "@/components/ui/toast";
import { mensajeDe } from "@/lib/errors";

import { useCreateUserReport, useImprovementsAvailability } from "./api";
import { capturarPantalla } from "./captura";
import { FichaMejora } from "./FichaMejora";
import { puedeGrabar, puedeGrabarPantalla, reloj, useGrabadora } from "./grabadora";

/**
 * **«Mejoremos juntos»: el botón flotante** (encima del botón de la barra «Reportar mejora», que
 * sigue igual como acceso directo). Un ícono de app cuadrado, blanco, con el isótopo de la **Mosaico
 * Andrea** (los cuatro pétalos a las esquinas) en negro. Se arrastra y se pega al borde más cercano; al
 * pasar por encima se ilumina y dice «Mejoremos juntos»; al tocarlo abre un menú con dos acciones:
 *
 * ```
 * 📸 Tomar captura   dibuja un recuadro rojo sobre la zona y manda la foto a /user-reports (como la
 *                    «Captura de pantalla» de la barra; el recuadro queda pintado en la imagen)
 * 🎥 Grabar video    pantalla + voz con la grabadora a prueba de cortes y la ficha → /improvements
 * ```
 *
 * No reinventa el envío: reusa `capturarPantalla`, `useCreateUserReport`, `useGrabadora` y `FichaMejora`
 * del mismo módulo de mejoras. El isótopo son los trazos exactos de la zona «petalos» del diseño
 * `mosaico-andrea` (`features/tile-designs/catalog-1.ts`), en el lienzo 0–100.
 */

/** Los cuatro pétalos de la Mosaico Andrea, copiados tal cual del catálogo (zona «petalos», 0–100). */
const ANDREA_PETALOS = [
  "M5.6 22L8.2 26.6L9.7 28.8L13 33L16.8 36.7L18.8 38.5L23.1 41.6L27.8 44.2L30.2 45.3L35.4 47.1L40.7 48.4L43.5 48.8L49.1 49.1L48.8 43.5L47.8 38L46.3 32.8L45.3 30.2L42.9 25.4L40.1 20.9L36.7 16.8L33 13L31 11.3L26.6 8.2L22 5.6L19.5 4.5L14.4 2.6L9.1 1.4L6.3 0L0 0L0 6.3L1.4 9.1L2.6 14.4L3.5 17Z",
  "M75.3 6.8L70.8 9.7L68.7 11.3L64.8 14.8L61.2 18.8L59.6 20.9L56.7 25.4L54.4 30.2L53.4 32.8L51.8 38L50.9 43.5L50.6 49.1L53.4 49L59 48.4L64.3 47.1L69.4 45.3L71.9 44.2L76.5 41.6L80.9 38.5L84.8 34.9L88.4 31L91.5 26.6L94.1 22L95.2 19.5L97 14.4L98.3 9.1L98.9 3.5L99 0L93.3 0L90.6 1.4L87.9 1.9L82.7 3.5L80.2 4.5Z",
  "M17 96.2L22 94.1L24.3 92.8L28.8 90L31 88.3L34.9 84.8L38.5 80.8L40.1 78.7L42.9 74.2L44.2 71.9L46.3 66.9L47.1 64.3L48.4 59L48.8 56.2L49.1 50.6L43.5 50.9L38 51.8L32.8 53.4L27.8 55.5L23.1 58.1L18.8 61.2L16.8 62.9L13 66.7L9.7 70.8L6.8 75.3L5.6 77.7L3.5 82.7L1.9 87.9L1.4 90.6L0 93.3L0 99L3.5 98.9L9.1 98.3L11.8 97.7Z",
  "M94.1 77.7L91.5 73L90 70.8L86.6 66.7L82.9 62.9L80.8 61.2L76.5 58.1L71.9 55.5L69.4 54.4L64.3 52.5L59 51.3L56.2 50.9L50.6 50.6L50.9 56.2L51.8 61.7L53.4 66.9L54.4 69.4L56.7 74.2L59.6 78.7L62.9 82.9L66.7 86.6L68.7 88.4L73 91.5L77.7 94.1L80.1 95.2L85.2 97L90.6 98.3L93.3 98.7L99 99L98.7 93.3L97.7 87.9L96.2 82.7Z",
];

const MARGIN = 14;
const SIZE = 60;
/** El rojo de NECA para señalar (el mismo de `captura.ts`). */
const ROJO = "#d64545";

type Punto = { x: number; y: number };
type Caja = { x: number; y: number; w: number; h: number };

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

export function BotonMejoremos() {
  const location = useLocation();
  const disponible = useImprovementsAvailability().data;
  const max = disponible?.max_seconds ?? 600;
  const { estado, empezar, detener, descartar } = useGrabadora(max);

  const [awake, setAwake] = useState(false);
  const [menu, setMenu] = useState(false);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number; origin: string }>({ left: 0, top: 0, origin: "bottom right" });
  const [bubbleBelow, setBubbleBelow] = useState(false);

  // Captura con recuadro rojo.
  const [dibujando, setDibujando] = useState(false);
  const [caja, setCaja] = useState<Caja | null>(null);
  const [captura, setCaptura] = useState<{ imagen: string | null; ruta: string; preparando: boolean; fallo: string | null } | null>(null);
  const [nota, setNota] = useState("");

  // Video.
  const [aviso, setAviso] = useState(false);
  const [ficha, setFicha] = useState(false);
  const [gracias, setGracias] = useState<ToastMessage | null>(null);

  const guardar = useCreateUserReport();
  const orbRef = useRef<HTMLDivElement | null>(null);
  const pos = useRef<Punto>({ x: 0, y: 0 });
  const menuRef = useRef(menu);
  menuRef.current = menu;

  const videoDisponible = Boolean(disponible?.enabled) && puedeGrabar() && puedeGrabarPantalla();
  const motivoVideo = !disponible?.enabled
    ? "Falta configurar dónde guardar las grabaciones"
    : !puedeGrabar()
      ? "Este navegador no deja grabar"
      : !puedeGrabarPantalla()
        ? "Este navegador no deja grabar la pantalla"
        : undefined;

  // ---- colocar, pegar al borde ----
  const colocar = (x: number, y: number) => {
    const orbe = orbRef.current;
    if (!orbe) return;
    const s = orbe.offsetWidth || SIZE;
    const nx = clamp(x, MARGIN, window.innerWidth - s - MARGIN);
    const ny = clamp(y, MARGIN, window.innerHeight - s - MARGIN);
    pos.current = { x: nx, y: ny };
    orbe.style.left = `${nx}px`;
    orbe.style.top = `${ny}px`;
    setBubbleBelow(ny < 90);
  };

  const pegar = () => {
    const orbe = orbRef.current;
    if (!orbe) return;
    const s = orbe.offsetWidth || SIZE;
    const cx = pos.current.x + s / 2;
    const cy = pos.current.y + s / 2;
    const dL = cx;
    const dR = window.innerWidth - cx;
    const dT = cy;
    const dB = window.innerHeight - cy;
    const min = Math.min(dL, dR, dT, dB);
    orbe.classList.add("bm-snapping");
    if (min === dL) colocar(MARGIN, pos.current.y);
    else if (min === dR) colocar(window.innerWidth - s - MARGIN, pos.current.y);
    else if (min === dT) colocar(pos.current.x, MARGIN);
    else colocar(pos.current.x, window.innerHeight - s - MARGIN);
    window.setTimeout(() => orbe.classList.remove("bm-snapping"), 340);
  };

  // Posición inicial: pegado a la derecha, a media altura. Y recolocar al cambiar el tamaño.
  useEffect(() => {
    const orbe = orbRef.current;
    if (!orbe) return;
    const s = orbe.offsetWidth || SIZE;
    colocar(window.innerWidth - s - MARGIN, window.innerHeight * 0.55);
    const onResize = () => colocar(pos.current.x, pos.current.y);
    window.addEventListener("resize", onResize);
    // Guiño de bienvenida.
    const t1 = window.setTimeout(() => setAwake(true), 700);
    const t2 = window.setTimeout(() => setAwake(false), 2500);
    return () => {
      window.removeEventListener("resize", onResize);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  // ---- arrastre del orbe ----
  useEffect(() => {
    const orbe = orbRef.current;
    if (!orbe) return;
    let arrastrando = false;
    let movido = false;
    let startX = 0;
    let startY = 0;
    let offX = 0;
    let offY = 0;

    const onDown = (e: PointerEvent) => {
      if (menuRef.current) return;
      arrastrando = true;
      movido = false;
      orbe.classList.remove("bm-snapping");
      orbe.classList.add("bm-dragging");
      startX = e.clientX;
      startY = e.clientY;
      offX = e.clientX - pos.current.x;
      offY = e.clientY - pos.current.y;
      setAwake(true);
      try {
        orbe.setPointerCapture(e.pointerId);
      } catch {
        /* sin captura de puntero no pasa nada */
      }
    };
    const onMove = (e: PointerEvent) => {
      if (!arrastrando) return;
      if (Math.abs(e.clientX - startX) > 5 || Math.abs(e.clientY - startY) > 5) movido = true;
      colocar(e.clientX - offX, e.clientY - offY);
      e.preventDefault();
    };
    const onUp = (e: PointerEvent) => {
      if (!arrastrando) return;
      arrastrando = false;
      orbe.classList.remove("bm-dragging");
      try {
        orbe.releasePointerCapture(e.pointerId);
      } catch {
        /* idem */
      }
      if (movido) pegar();
      else abrirMenu();
    };

    orbe.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      orbe.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- menú ----
  function abrirMenu() {
    setAwake(true);
    const s = orbRef.current?.offsetWidth || SIZE;
    const mw = 224;
    const mh = 150;
    const cx = pos.current.x + s / 2;
    const cy = pos.current.y + s / 2;
    let left = cx > window.innerWidth / 2 ? pos.current.x + s - mw : pos.current.x;
    let top = cy > window.innerHeight / 2 ? pos.current.y - mh - 12 : pos.current.y + s + 12;
    left = clamp(left, MARGIN, window.innerWidth - mw - MARGIN);
    top = clamp(top, MARGIN, window.innerHeight - mh - MARGIN);
    const origin = `${cy > window.innerHeight / 2 ? "bottom" : "top"} ${cx > window.innerWidth / 2 ? "right" : "left"}`;
    setMenuPos({ left, top, origin });
    setMenu(true);
  }
  function cerrarMenu() {
    setMenu(false);
    setAwake(false);
  }

  // Cerrar el menú al tocar fuera.
  useEffect(() => {
    if (!menu) return;
    const fuera = (e: PointerEvent) => {
      const t = e.target as Node;
      if (orbRef.current?.contains(t)) return;
      if ((t as Element).closest?.("[data-bm-menu]")) return;
      cerrarMenu();
    };
    document.addEventListener("pointerdown", fuera, true);
    return () => document.removeEventListener("pointerdown", fuera, true);
  }, [menu]);

  // ---- flujo captura: dibujar recuadro rojo, hornearlo en la foto, mandar a /user-reports ----
  const inicio = useRef<Punto | null>(null);
  /** La captura se hornea en segundo plano; aquí se guarda su promesa por si se envía antes de terminar. */
  const capturaPromise = useRef<Promise<string | null> | null>(null);
  function empezarCaptura() {
    cerrarMenu();
    setCaja(null);
    setDibujando(true);
  }
  function cancelarDibujo() {
    setDibujando(false);
    setCaja(null);
    inicio.current = null;
  }
  function terminarDibujo() {
    // El panel sale AL INSTANTE con un placeholder; la captura (con el recuadro rojo horneado) se
    // genera APARTE, en segundo plano, mientras la persona ya escribe. El panel es `data-sin-captura`,
    // así que no sale en la foto; el recuadro SÍ (no lleva esa marca).
    const ruta = location.pathname;
    setDibujando(false);
    inicio.current = null;
    setNota("");
    setCaptura({ imagen: null, ruta, preparando: true, fallo: null });

    const p = (async (): Promise<string | null> => {
      await new Promise<void>((listo) => requestAnimationFrame(() => requestAnimationFrame(() => listo())));
      try {
        return await capturarPantalla();
      } catch {
        return null;
      }
    })();
    capturaPromise.current = p;
    void p.then((imagen) => {
      setCaja(null);
      setCaptura((prev) =>
        prev ? { ...prev, imagen, preparando: false, fallo: imagen ? null : "No se pudo generar la captura; puedes enviar solo el texto." } : prev,
      );
    });
  }

  async function enviarCaptura() {
    if (!captura || guardar.isPending) return;
    let imagen = captura.imagen;
    // Si todavía no terminó de hornearse (raro), se espera lo mínimo necesario a que termine.
    if (imagen === null && capturaPromise.current) imagen = await capturaPromise.current;
    try {
      const reporte = await guardar.mutateAsync({
        kind: "screenshot",
        screen_path: captura.ruta,
        text: nota.trim() || null,
        element: null,
        screenshot: imagen,
      });
      setCaptura(null);
      setGracias({ id: Date.now(), text: `${reporte.code} guardada. Gracias por contarla.` });
    } catch {
      // El servidor dice el motivo bajo el botón; lo escrito se queda para reintentar.
    }
  }

  // ---- flujo video (igual que «Reportar mejora», solo la rama de video) ----
  function arrancarVideo() {
    setAviso(false);
    setFicha(true);
    void empezar(false, `${location.pathname}${location.search}`);
  }

  const grabando = estado.fase === "grabando";

  return (
    <>
      <style>{CSS}</style>

      {/* El orbe, portaleado a `document.body` para ESCAPAR del `inert` que las apps a pantalla
          completa (Cotizador, NECA Planta: `FullScreenApp`) ponen sobre `#root`. Dentro de `#root`
          el orbe se vería pero no recibiría clics. `data-sin-captura` para que no salga en la foto. */}
      {createPortal(
        <div data-sin-captura="" className="bm-layer">
          <div
            ref={orbRef}
          role="button"
          tabIndex={0}
          aria-label="Mejoremos juntos: reportar una mejora"
          className={`bm-orb${awake ? " bm-awake" : ""}${bubbleBelow ? " bm-bubble-below" : ""}`}
          onPointerEnter={() => setAwake(true)}
          onPointerLeave={() => {
            if (!menu && !grabando) setAwake(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              abrirMenu();
            }
          }}
        >
          <span className="bm-gloss" />
          <svg className="bm-logo" viewBox="0 0 100 100" aria-hidden="true">
            <g fill="#141414">
              {ANDREA_PETALOS.map((d) => (
                <path key={d.slice(0, 12)} d={d} />
              ))}
            </g>
          </svg>
            <div className="bm-bubble">Mejoremos juntos</div>
          </div>
        </div>,
        document.body,
      )}

      {/* Menú de dos acciones. */}
      {menu
        ? createPortal(
            <div
              data-sin-captura=""
              data-bm-menu=""
              role="menu"
              aria-label="Acciones de mejora"
              className="bm-menu"
              style={{ left: menuPos.left, top: menuPos.top, transformOrigin: menuPos.origin }}
            >
              <div className="bm-mtitle">Reportar una mejora</div>
              <button type="button" role="menuitem" className="bm-item" onClick={empezarCaptura}>
                <span className="bm-mi bm-cap">📸</span>
                <span>
                  Tomar captura
                  <span className="bm-msub">Señala la zona exacta</span>
                </span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="bm-item"
                disabled={!videoDisponible}
                title={motivoVideo}
                onClick={() => {
                  cerrarMenu();
                  setAviso(true);
                }}
              >
                <span className="bm-mi bm-vid">🎥</span>
                <span>
                  Grabar video
                  <span className="bm-msub">{motivoVideo ?? "Muestra el problema en vivo"}</span>
                </span>
              </button>
            </div>,
            document.body,
          )
        : null}

      {/* Dibujar el recuadro rojo. La capa oscura y el aviso NO se capturan; el recuadro SÍ. */}
      {dibujando
        ? createPortal(
            <div
              data-sin-captura=""
              className="bm-shade"
              onPointerDown={(e) => {
                inicio.current = { x: e.clientX, y: e.clientY };
                setCaja({ x: e.clientX, y: e.clientY, w: 0, h: 0 });
              }}
              onPointerMove={(e) => {
                if (!inicio.current) return;
                const i = inicio.current;
                setCaja({
                  x: Math.min(e.clientX, i.x),
                  y: Math.min(e.clientY, i.y),
                  w: Math.abs(e.clientX - i.x),
                  h: Math.abs(e.clientY - i.y),
                });
              }}
              onPointerUp={() => {
                const c = caja;
                if (c && c.w >= 16 && c.h >= 16) terminarDibujo();
                else cancelarDibujo();
              }}
            >
              <div className="bm-hint">
                <span className="bm-k" /> Dibuja un recuadro sobre la zona a mejorar
              </div>
              <button type="button" className="bm-cancel" aria-label="Cancelar" onPointerDown={(e) => e.stopPropagation()} onClick={cancelarDibujo}>
                ✕
              </button>
            </div>,
            document.body,
          )
        : null}

      {/* El recuadro rojo: fuera de la capa, SIN `data-sin-captura`, para que quede pintado en la foto. */}
      {caja
        ? createPortal(
            <div className="bm-redbox" style={{ left: caja.x, top: caja.y, width: caja.w, height: caja.h }}>
              <span className="bm-tag">Zona señalada</span>
            </div>,
            document.body,
          )
        : null}

      {/* Panel FLOTANTE de la captura → /user-reports. Aparece AL INSTANTE al soltar el recuadro
          (es `data-sin-captura`, no sale en la foto). La miniatura pasa de «preparando…» a la imagen
          cuando la captura termina de hornearse en segundo plano. Enviar queda habilitado de una. */}
      {captura
        ? createPortal(
            <div data-sin-captura="" className="bm-panel" role="dialog" aria-label="Tomar captura">
              <div className="bm-panel-head">
                <strong>Tomar captura</strong>
                <button type="button" className="bm-panel-x" aria-label="Cerrar" onClick={() => setCaptura(null)}>✕</button>
              </div>
              <form
                className="bm-panel-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void enviarCaptura();
                }}
              >
                <p className="bm-panel-ruta">
                  En <code>{captura.ruta}</code>
                </p>
                <div className="bm-thumb">
                  {captura.imagen ? (
                    <img src={captura.imagen} alt="Captura con la zona señalada" />
                  ) : captura.fallo ? (
                    <span className="bm-thumb-msg">{captura.fallo}</span>
                  ) : (
                    <span className="bm-thumb-msg bm-thumb-loading">Preparando captura…</span>
                  )}
                </div>
                <textarea
                  className="bm-ta"
                  placeholder="¿Qué hay que mirar? (opcional)"
                  value={nota}
                  maxLength={2000}
                  autoFocus
                  onChange={(e) => setNota(e.target.value)}
                />
                {guardar.error ? <p role="alert" className="bm-err">{mensajeDe(guardar.error)}</p> : null}
                <div className="bm-panel-actions">
                  <button type="submit" className="bm-btn bm-btn-primary" disabled={guardar.isPending}>
                    {guardar.isPending ? "Enviando…" : "Enviar"}
                  </button>
                  <button type="button" className="bm-btn bm-btn-sec" disabled={guardar.isPending} onClick={() => setCaptura(null)}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>,
            document.body,
          )
        : null}

      {/* Aviso de privacidad antes de grabar. */}
      <Sheet open={aviso} onOpenChange={setAviso} title="Grabar video" description="Se grabará tu pantalla y tu voz; solo la verá el dueño del ERP.">
        <div className="grid gap-4 text-sm text-ink-2">
          <p>Muestra lo que encontraste y cuéntalo en voz alta. Hasta {Math.round(max / 60)} minutos: al llegar, se detiene sola.</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={arrancarVideo}>
              Empezar a grabar
            </Button>
            <Button type="button" variant="secondary" onClick={() => setAviso(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Sheet>

      {/* Grabando: contador y «Detener», abajo a la derecha, fuera del ERP. */}
      {estado.fase === "grabando"
        ? createPortal(
            <div data-sin-captura="" role="status" aria-label="Grabación en curso" className="fixed right-4 bottom-4 z-[2147483000] flex items-center gap-3 rounded-lg bg-surface px-3.5 py-2.5 text-sm text-ink shadow-soft">
              <span className="inline-block size-3 rounded-full" style={{ background: ROJO }} />
              <span className="tabular-nums">
                Grabando {reloj(estado.segundos)} / {reloj(max)}
              </span>
              {estado.conVoz ? null : <span className="text-warn">sin micrófono</span>}
              <Button type="button" variant="danger" className="min-h-9" onClick={() => detener()}>
                Detener
              </Button>
            </div>,
            document.body,
          )
        : null}

      {estado.fase === "error"
        ? createPortal(
            <div data-sin-captura="" role="alert" className="fixed right-4 bottom-4 z-[2147483000] flex max-w-sm items-center gap-3 rounded-lg bg-surface px-3.5 py-2.5 text-sm text-ink shadow-soft">
              <span>{estado.mensaje}</span>
              <button type="button" className="font-semibold underline" onClick={descartar}>
                Cerrar
              </button>
            </div>,
            document.body,
          )
        : null}

      {/* Ficha del video → /improvements. */}
      {estado.fase === "grabada" ? (
        <Sheet open={ficha} onOpenChange={setFicha} title="Guardar la mejora" description="La grabación quedó lista. Cuéntale al administrador qué es.">
          <FichaMejora
            archivo={estado.archivo}
            segundos={estado.segundos}
            ruta={estado.ruta}
            maxBytes={disponible?.max_bytes ?? 95 * 1024 * 1024}
            onGuardada={(codigo) => {
              descartar();
              setFicha(false);
              setGracias({ id: Date.now(), text: `${codigo ?? "Mejora"} guardada. Gracias por contarla.` });
            }}
            onDescartar={() => {
              descartar();
              setFicha(false);
            }}
          />
        </Sheet>
      ) : null}

      {gracias ? <Toast message={gracias} onDone={() => setGracias(null)} /> : null}
    </>
  );
}

const CSS = `
.bm-layer{ position:fixed; inset:0; z-index:2147483000; pointer-events:none; }
.bm-orb{
  position:fixed; left:0; top:0; width:60px; height:60px; border-radius:26%;
  pointer-events:auto; cursor:grab; touch-action:none; z-index:2147483001;
  display:grid; place-items:center;
  background:radial-gradient(circle at 36% 30%, #ffffff, #f4f6fa 52%, #e6eaf1 100%);
  box-shadow:0 0 0 1px rgba(17,24,32,.10) inset, 0 6px 18px rgba(20,30,50,.22), 0 2px 6px rgba(20,30,50,.14);
  transition:transform .18s ease, box-shadow .25s ease, filter .25s;
}
.bm-snapping{ transition:left .32s cubic-bezier(.22,1,.36,1), top .32s cubic-bezier(.22,1,.36,1), transform .18s ease, box-shadow .25s; }
.bm-dragging{ cursor:grabbing; transition:transform .05s; }
.bm-awake{ transform:scale(1.08); filter:brightness(1.05);
  box-shadow:0 0 0 1px rgba(17,24,32,.14) inset, 0 10px 26px rgba(20,30,50,.26), 0 0 0 6px rgba(255,255,255,.6), 0 0 24px rgba(255,255,255,.95), 0 0 50px rgba(190,205,230,.65); }
.bm-orb::after{ content:""; position:absolute; inset:-4px; border-radius:32%; border:1.5px solid rgba(120,140,170,.35); opacity:0; }
.bm-awake::after{ animation:bm-pulse 2.4s ease-out infinite; }
@keyframes bm-pulse{ 0%{ transform:scale(.92); opacity:.6; } 70%{ transform:scale(1.35); opacity:0; } 100%{ opacity:0; } }
.bm-gloss{ position:absolute; top:7px; left:13px; width:28px; height:17px; border-radius:50%; background:linear-gradient(180deg,rgba(255,255,255,.9),rgba(255,255,255,0)); filter:blur(.4px); pointer-events:none; }
.bm-logo{ width:68%; height:68%; display:block; pointer-events:none; transition:transform .55s cubic-bezier(.22,1,.36,1); }
.bm-awake .bm-logo{ transform:rotate(45deg); }
.bm-bubble{ position:absolute; bottom:calc(100% + 12px); left:50%; transform:translateX(-50%) translateY(6px);
  background:#141b30; color:#fff; padding:8px 13px; border-radius:12px; font-size:13px; font-weight:700; white-space:nowrap;
  box-shadow:0 10px 30px rgba(24,39,75,.28); opacity:0; pointer-events:none; transition:opacity .2s, transform .2s; }
.bm-bubble::after{ content:""; position:absolute; top:100%; left:50%; transform:translateX(-50%); border:6px solid transparent; border-top-color:#141b30; }
.bm-awake .bm-bubble{ opacity:1; transform:translateX(-50%) translateY(0); }
.bm-bubble-below .bm-bubble{ bottom:auto; top:calc(100% + 12px); }
.bm-bubble-below .bm-bubble::after{ top:auto; bottom:100%; border-top-color:transparent; border-bottom-color:#141b30; }
.bm-menu{ position:fixed; z-index:2147483003; min-width:216px; background:rgba(255,255,255,.96);
  backdrop-filter:blur(16px) saturate(1.4); -webkit-backdrop-filter:blur(16px) saturate(1.4);
  border:1px solid #e2e8f0; border-radius:16px; box-shadow:0 14px 40px rgba(24,39,75,.18); padding:7px;
  animation:bm-pop .16s ease; color:#1a2437; }
@keyframes bm-pop{ from{ opacity:0; transform:scale(.9) translateY(6px); } to{ opacity:1; transform:scale(1) translateY(0); } }
.bm-mtitle{ font-size:11px; color:#8a94a8; font-weight:700; text-transform:uppercase; letter-spacing:.5px; padding:7px 10px 4px; }
.bm-item{ display:flex; align-items:center; gap:12px; width:100%; border:0; background:transparent; color:#1a2437; cursor:pointer;
  padding:11px; border-radius:11px; font-size:14.5px; font-weight:600; text-align:left; font-family:inherit; }
.bm-item:hover:not(:disabled){ background:#f1f4f9; }
.bm-item:disabled{ opacity:.5; cursor:not-allowed; }
.bm-mi{ width:36px; height:36px; border-radius:10px; flex:none; display:grid; place-items:center; font-size:18px; }
.bm-cap{ background:rgba(47,107,255,.14); }
.bm-vid{ background:rgba(214,69,69,.14); }
.bm-msub{ display:block; font-size:11.5px; color:#8a94a8; font-weight:500; margin-top:1px; }
.bm-shade{ position:fixed; inset:0; z-index:2147483000; background:rgba(8,12,24,.5); cursor:crosshair; touch-action:none; }
.bm-hint{ position:absolute; top:22px; left:50%; transform:translateX(-50%); background:rgba(255,255,255,.96); color:#1a2437;
  padding:11px 18px; border-radius:999px; font-size:14px; font-weight:700; box-shadow:0 8px 24px rgba(0,0,0,.3); display:flex; align-items:center; gap:9px; }
.bm-k{ background:${ROJO}; width:14px; height:14px; border-radius:4px; display:inline-block; }
.bm-cancel{ position:absolute; top:20px; right:18px; background:rgba(255,255,255,.16); color:#fff; border:1px solid rgba(255,255,255,.3);
  width:40px; height:40px; border-radius:50%; font-size:18px; cursor:pointer; }
.bm-redbox{ position:fixed; z-index:2147483002; border:3px solid ${ROJO}; border-radius:4px; background:rgba(214,69,69,.12); box-shadow:0 0 18px rgba(214,69,69,.5); pointer-events:none; }
.bm-tag{ position:absolute; top:-24px; left:0; background:${ROJO}; color:#fff; font-size:11px; font-weight:700; padding:3px 8px; border-radius:6px; white-space:nowrap; }
.bm-panel{ position:fixed; z-index:2147483004; right:18px; bottom:18px; width:min(380px,92vw); max-height:88vh; overflow:auto;
  background:#ffffff; color:#1a2437; border:1px solid #e2e8f0; border-radius:16px; box-shadow:0 20px 56px rgba(24,39,75,.26);
  padding:14px 16px 16px; pointer-events:auto; animation:bm-pop .12s ease; }
.bm-panel-head{ display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:4px; }
.bm-panel-head strong{ font-size:16px; letter-spacing:-.2px; }
.bm-panel-x{ border:0; background:transparent; color:#8a94a8; font-size:17px; cursor:pointer; width:30px; height:30px; border-radius:8px; }
.bm-panel-x:hover{ background:#f1f4f9; color:#1a2437; }
.bm-panel-form{ display:grid; gap:11px; }
.bm-panel-ruta{ margin:0; font-size:12.5px; color:#566178; }
.bm-panel-ruta code{ background:#f1f4f9; padding:1px 6px; border-radius:6px; font-size:12px; }
.bm-thumb{ min-height:120px; display:grid; place-items:center; border:1px solid #eef2f7; border-radius:12px; background:#f7f9fc; overflow:hidden; }
.bm-thumb img{ display:block; width:100%; max-height:200px; object-fit:contain; }
.bm-thumb-msg{ font-size:13px; color:#8a94a8; font-weight:600; padding:16px; text-align:center; }
.bm-thumb-loading{ position:relative; }
.bm-thumb-loading::after{ content:""; display:inline-block; margin-left:8px; width:12px; height:12px; border-radius:50%;
  border:2px solid #c7d0e0; border-top-color:${ROJO}; vertical-align:-2px; animation:bm-spin .7s linear infinite; }
@keyframes bm-spin{ to{ transform:rotate(360deg); } }
.bm-ta{ width:100%; min-height:72px; resize:vertical; font-family:inherit; font-size:14px; line-height:1.4;
  padding:10px 12px; border-radius:12px; border:1px solid #e2e8f0; background:#fbfcfe; color:#1a2437; }
.bm-ta:focus{ outline:none; border-color:#2f6bff; box-shadow:0 0 0 3px rgba(47,107,255,.18); }
.bm-err{ margin:0; font-size:13px; color:${ROJO}; font-weight:600; }
.bm-panel-actions{ display:flex; gap:9px; }
.bm-btn{ border:0; border-radius:11px; padding:11px 16px; font-weight:800; font-size:14.5px; cursor:pointer; font-family:inherit; }
.bm-btn:disabled{ opacity:.6; cursor:default; }
.bm-btn-primary{ background:#2f6bff; color:#fff; flex:1; }
.bm-btn-primary:hover:not(:disabled){ background:#1e4fd6; }
.bm-btn-sec{ background:#f1f4f9; color:#566178; }
.bm-btn-sec:hover:not(:disabled){ background:#e6ebf3; }
@media (prefers-reduced-motion:reduce){ .bm-orb, .bm-logo, .bm-bubble{ transition:none; } .bm-awake::after{ animation:none; opacity:0; } .bm-awake .bm-logo{ transform:none; } }
`;
